import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@stockline/ui/components/button";
import type { GuideAdvance, GuideStep } from "@/lib/guide/types";
import type { TrackedRect } from "@/lib/guide/useTrackedRect";

const CALLOUT_W = 320;
const GAP = 10;
const VIEWPORT_MARGIN = 8;

interface GuideCalloutProps {
  readonly step: GuideStep;
  readonly stepIndex: number;
  readonly stepCount: number;
  /** Anchor rect; null → centered card (no-target / navigation / soft-miss steps). */
  readonly anchor: TrackedRect | null;
  readonly advanceOn: GuideAdvance;
  readonly isLastStep: boolean;
  readonly onNext: () => void;
  readonly onDismiss: () => void;
  /** Replaces the hint with an action button (e.g. "Take me there"). */
  readonly action?: { label: string; onClick: () => void };
}

/**
 * Callout card. CRITICAL: the card is `pointer-events-none` so clicks pass
 * straight through to the real UI underneath — the guide never intercepts the
 * user's action, and (being portaled to body) never triggers Radix
 * outside-click dismissal of a popover the step lives in. Only the small
 * interactive affordances (close ×, and the Next/action button on info steps)
 * re-enable pointer events.
 *
 * Positioned like the house manual-popover pattern (KpiTrendPopover): fixed +
 * measured flip/clamp against the viewport, portaled by the parent.
 */
export function GuideCallout({
  step,
  stepIndex,
  stepCount,
  anchor,
  advanceOn,
  isLastStep,
  onNext,
  onDismiss,
  action,
}: GuideCalloutProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  useLayoutEffect(() => {
    if (!anchor) {
      setPos(null);
      return;
    }
    const height = ref.current?.offsetHeight ?? 160;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = anchor.left + anchor.width / 2 - CALLOUT_W / 2;
    let y = anchor.top + anchor.height + GAP;
    if (y + height > vh - VIEWPORT_MARGIN) {
      y = anchor.top - height - GAP; // no room below — flip above
    }
    y = Math.max(VIEWPORT_MARGIN, y);
    x = Math.min(
      Math.max(VIEWPORT_MARGIN, x),
      Math.max(VIEWPORT_MARGIN, vw - CALLOUT_W - VIEWPORT_MARGIN),
    );
    setPos({ x, y });
  }, [anchor, step]);

  const centered = anchor === null;
  // A Next button appears ONLY for info steps (advanceOn "next") or an explicit
  // action. Action-driven steps (click-target / navigate) let the user do the
  // real thing — no button that could dismiss a popover or add friction.
  const showNext = !action && advanceOn === "next";

  // The single footer button: an explicit action ("Take me there"), else a
  // Next/Done on info steps, else nothing. Built here (not a nested JSX ternary).
  let footerButton: ReactNode = null;
  if (action) {
    footerButton = (
      <Button
        size="sm"
        data-testid="guide-action-button"
        className="pointer-events-auto bg-background text-guide hover:bg-guide/10"
        onClick={action.onClick}
      >
        {action.label}
      </Button>
    );
  } else if (showNext) {
    footerButton = (
      <Button
        size="sm"
        data-testid="guide-next-button"
        className="pointer-events-auto bg-background text-guide hover:bg-guide/10"
        onClick={onNext}
      >
        {isLastStep ? t("guide.done") : t("guide.next")}
      </Button>
    );
  }

  const card = (
    <div
      ref={ref}
      data-testid="guide-callout"
      role="dialog"
      aria-label={step.title ?? t("guide.defaultTitle")}
      className="w-[320px] rounded-lg border border-guide bg-guide text-guide-foreground shadow-xl shadow-black/25 p-4 pointer-events-none select-none"
      style={
        centered
          ? undefined
          : {
              position: "fixed",
              left: pos?.x ?? 0,
              top: pos?.y ?? 0,
              visibility: pos ? "visible" : "hidden",
              zIndex: 10060,
            }
      }
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-semibold text-guide-foreground">
          {step.title ?? t("guide.defaultTitle")}
        </div>
        <button
          type="button"
          data-testid="guide-close-button"
          aria-label={t("guide.close")}
          onClick={onDismiss}
          className="pointer-events-auto text-guide-foreground/80 hover:text-guide-foreground -mt-1 -mr-1 p-1"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <p className="mt-1.5 text-sm text-guide-foreground whitespace-pre-wrap">
        {step.body}
      </p>

      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-guide-foreground/80 tabular-nums">
          {stepCount > 1 ? `${stepIndex + 1}/${stepCount}` : ""}
        </span>
        {footerButton}
      </div>
    </div>
  );

  if (!centered) return card;
  return (
    <div className="fixed inset-0 z-[10060] flex items-center justify-center pointer-events-none">
      {card}
    </div>
  );
}
